/*
 * Copyright (c) 2021 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import * as StateManager from '../../store/state/state-manager';
import * as actions from '../../store/state/actions/action-creators';
import {
  addActionsToParentContext,
  addPreviewResetAction,
  getActionPayload,
  processDataToDispatchErrorAction,
  processViewResponse
} from '../../store/state/epics/util';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import {
  getCaseInfoID,
  getContainerNameFromContainerItemID,
  isErrorActionType,
  isParentAndChildContextHoldingSameItem,
  updateKeyAndFlowName,
  setLastRefreshTimeProperty,
  promiseHandler,
  deferCaseSummaryViewUpdate
} from '../common-helper';
import { publishAssignmentSubmissionDoneEvent } from '../common-events';
import { getDataContextName, getTargetFromContainerItemID } from '../../container/container-utils';
import Constants, { publicConstants } from '../../constants';
import { clearContextMessagesInActiveWorkArea } from '../../messages';
import DataApi from '../../data-view';
import Utils from '../../utils/utils';
import type { Action } from '../../store/state/state-manager';
import CaseUtils from '../utils';

import { NAVIGATE_TO_STEP } from './action-creator';
import { executeNavigateToStepRequest } from './async-request';

const { RESOURCE_TYPES } = publicConstants;

export const navigateToStep = (action$: Observable<Action>, state$: { value: any }) => {
  let actionMgrID = 0;
  let actionsOut: any = [];

  return action$.pipe(
    ofType(NAVIGATE_TO_STEP),
    switchMap((action: Action) => {
      ({ actionMgrID } = action.payload);
      return of(action.payload).pipe(
        mergeMap((actionInPayload) => {
          clearContextMessagesInActiveWorkArea(actionInPayload.context);
          return executeNavigateToStepRequest(actionInPayload, { state$ });
        }),
        mergeMap(async (chainedData) => {
          if (chainedData && isErrorActionType(chainedData.type)) {
            actionsOut.push(processDataToDispatchErrorAction(chainedData));
            return actionsOut;
          }

          const { actionInPayload, response, headers } = chainedData;
          const { containerItemID, context = getDataContextName(containerItemID), isModalAction } = actionInPayload;

          // clear DataApi caches after success
          DataApi.clearContextedCache(actionInPayload.context);

          const target = getTargetFromContainerItemID(containerItemID);
          const containerName = getContainerNameFromContainerItemID(containerItemID);

          // this is the current container info from where the action got triggered, we shouldn't alter this object
          const currentContainerInfo = Object.freeze({
            target,
            containerItemID,
            context,
            containerName
          });

          const content = response?.data?.caseInfo?.content;
          const uiConfigName = response?.uiResources?.root?.config?.name;
          const uiConfigContext = response?.uiResources?.root?.config?.context;

          if (content && !content.pyViewName && uiConfigName && actionInPayload.viewType !== 'page') {
            Utils.setIn(content, 'pyViewName', uiConfigName);
            Utils.setIn(content, 'pyViewContext', uiConfigContext);
          }

          const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
          // [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic
          const options = { acName: target, ...currentContainerInfo };

          await processViewResponse(translatedDXAPIResponse, actionInPayload, options);

          const payload = getActionPayload(translatedDXAPIResponse, {
            ...currentContainerInfo,
            headers
          });
          if (!response?.nextAssignmentInfo) {
            payload.caseViewMode = Constants.REVIEW_MODE;
          }
          deferCaseSummaryViewUpdate(chainedData, actionsOut);
          payload.doDeleteCaseSummary = actionInPayload.doDeleteCaseSummary;
          payload.resourceType = RESOURCE_TYPES.ASSIGNMENT;
          // updating key and flowName
          updateKeyAndFlowName(payload, translatedDXAPIResponse);

          // Attachments widget using it
          setLastRefreshTimeProperty(payload.data, actionInPayload);
          const stages = translatedDXAPIResponse.data?.caseInfo?.stages;
          const stageID = translatedDXAPIResponse.data?.caseInfo?.stageID;
          publishAssignmentSubmissionDoneEvent({
            caseID: getCaseInfoID(translatedDXAPIResponse),
            isModalAction,
            stages,
            stageID
          });

          if (isParentAndChildContextHoldingSameItem(containerItemID)) {
            const propertiesToReplace = CaseUtils.getPropertiesToReplace(containerItemID);
            addActionsToParentContext(payload, actionsOut, '', { propertiesToReplace });
          }

          // updating the container item with previous assignment details
          actionsOut.push(actions.postProcess(actions.updateContainerItem, payload));

          // reset preview container is active
          addPreviewResetAction(actionsOut);
          return actionsOut;
        }),
        map(() => {
          StateManager.dispatch(actionsOut);
          promiseHandler(actionsOut, actionMgrID);
          actionsOut = [];
          return actionsOut;
        })
      );
    })
  );
};

export default navigateToStep;
